/**
 * The Good Patch
 * Custom storefront JavaScript
 *
 * 1. Normalizes Recharge delivery-frequency wording.
 * 2. Opens PDP galleries on media position #2.
 */

(() => {
  'use strict';

  /* =========================================================
     RECHARGE — NORMALIZE DELIVERY-FREQUENCY WORDING
     ========================================================= */

  const RECHARGE_WIDGET_SELECTOR = 'recharge-subscription-widget';

  const queryDeepAll = (root, selector, results = []) => {
    if (!root || typeof root.querySelectorAll !== 'function') {
      return results;
    }

    root.querySelectorAll(selector).forEach((element) => {
      results.push(element);
    });

    root.querySelectorAll('*').forEach((element) => {
      if (element.shadowRoot) {
        queryDeepAll(element.shadowRoot, selector, results);
      }
    });

    return results;
  };

  const parseMoney = (value) => {
    if (!value) return null;

    const cleaned = String(value)
      .replace(/[^0-9.,-]/g, '')
      .replace(/,(?=\d{2}$)/, '.')
      .replace(/,/g, '');

    const number = Number.parseFloat(cleaned);

    return Number.isFinite(number) ? number : null;
  };

  const calculateRechargeDiscount = (root) => {
    const originalElement = queryDeepAll(
      root,
      '[part~="rc-purchase-option__original-price"]'
    )[0];

    const discountedElement = queryDeepAll(
      root,
      '[part~="rc-purchase-option__discounted-price"]'
    )[0];

    const original = parseMoney(originalElement?.textContent);
    const discounted = parseMoney(discountedElement?.textContent);

    if (
      original === null ||
      discounted === null ||
      original <= 0 ||
      discounted >= original
    ) {
      return '';
    }

    const percentage = Math.round(
      ((original - discounted) / original) * 100
    );

    return `${percentage}% off`;
  };

  const normalizeRechargeFrequency = (
    text,
    fallbackDiscount
  ) => {
    const originalText = String(text || '').trim();

    const intervalMatch = originalText.match(
      /(?:deliver\s+every\s+)?(\d+)\s+(day|week|month|year)s?/i
    );

    if (!intervalMatch) {
      return originalText;
    }

    const amount = Number(intervalMatch[1]);
    const unit = intervalMatch[2].toLowerCase();

    const intervalText =
      amount === 1
        ? `Deliver every ${unit}`
        : `Deliver every ${amount} ${unit}s`;

    const displayedDiscount = originalText.match(
      /\d+(?:\.\d+)?%\s*(?:off)?/i
    );

    const discountText = displayedDiscount
      ? `${displayedDiscount[0]
          .replace(/\s*off$/i, '')
          .trim()} off`
      : fallbackDiscount;

    return discountText
      ? `${intervalText}, ${discountText}`
      : intervalText;
  };

  const updateRechargeWidget = (widget) => {
    if (!widget?.shadowRoot) return;

    const discount = calculateRechargeDiscount(
      widget.shadowRoot
    );

    const selects = queryDeepAll(
      widget.shadowRoot,
      'select[part~="rc-plans-select"], [part~="rc-plans-select"]'
    );

    selects.forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;

      Array.from(select.options).forEach((option) => {
        const updatedText = normalizeRechargeFrequency(
          option.textContent,
          discount
        );

        if (option.textContent !== updatedText) {
          option.textContent = updatedText;
        }
      });
    });
  };

  const refreshRechargeWidgets = () => {
    document
      .querySelectorAll(RECHARGE_WIDGET_SELECTOR)
      .forEach(updateRechargeWidget);
  };

  let rechargeRefreshTimer = null;

  const scheduleRechargeRefresh = () => {
    window.clearTimeout(rechargeRefreshTimer);

    rechargeRefreshTimer = window.setTimeout(() => {
      refreshRechargeWidgets();
    }, 100);
  };

  const rechargeObserver = new MutationObserver(
    scheduleRechargeRefresh
  );

  const initializeRecharge = () => {
    rechargeObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    refreshRechargeWidgets();

    [250, 750, 1500, 3000].forEach((delay) => {
      window.setTimeout(refreshRechargeWidgets, delay);
    });

    document.addEventListener(
      'change',
      scheduleRechargeRefresh,
      true
    );

    if ('customElements' in window) {
      customElements
        .whenDefined(RECHARGE_WIDGET_SELECTOR)
        .then(scheduleRechargeRefresh)
        .catch(() => {});
    }
  };


  /* =========================================================
     PDP — OPEN GALLERY ON MEDIA POSITION #2
     Keeps media #1 as the collection-card image.
     ========================================================= */

  const PRODUCT_TEMPLATE_CLASS = 'template-product';
  const MAX_GALLERY_ATTEMPTS = 30;
  const GALLERY_RETRY_DELAY = 150;

  const isProductPage = () =>
    document.body.classList.contains(
      PRODUCT_TEMPLATE_CLASS
    );

  const getMainGalleryElement = () =>
    document.querySelector(
      '.template-product .wt-product__gallery [data-gallery]'
    ) ||
    document.querySelector(
      '.template-product .wt-product__gallery .swiper'
    );

  const getGallerySwiper = () => {
    const gallery = getMainGalleryElement();

    if (!gallery) return null;

    return (
      gallery.swiper ||
      gallery.closest('.swiper')?.swiper ||
      gallery.querySelector('.swiper')?.swiper ||
      null
    );
  };

  const getGalleryThumbnails = () =>
    Array.from(
      document.querySelectorAll(
        '.template-product ' +
          '.wt-product__gallery [data-thumbs] ' +
          '.wt-slider__slide, ' +
        '.template-product ' +
          '.wt-product__gallery [data-thumbs] ' +
          '.swiper-slide'
      )
    );

  const openSecondProductMedia = () => {
    if (!isProductPage()) return true;

    const swiper = getGallerySwiper();

    if (swiper?.slides?.length > 1) {
      /*
       * Position #2 has zero-based index 1.
       * Use slideToLoop when the theme enables loop mode.
       */
      if (
        swiper.params?.loop &&
        typeof swiper.slideToLoop === 'function'
      ) {
        swiper.slideToLoop(1, 0, false);
      } else if (typeof swiper.slideTo === 'function') {
        swiper.slideTo(1, 0, false);
      }

      return true;
    }

    const thumbnails = getGalleryThumbnails();

    if (thumbnails.length > 1) {
      const secondThumbnail = thumbnails[1];
      const interactiveElement =
        secondThumbnail.querySelector(
          'button, a, [role="button"]'
        );

      if (interactiveElement) {
        interactiveElement.click();
      } else {
        secondThumbnail.click();
      }

      return true;
    }

    return false;
  };

  let galleryInitializationTimer = null;

  const initializeSecondProductMedia = () => {
    if (!isProductPage()) return;

    window.clearInterval(galleryInitializationTimer);

    let attempts = 0;

    galleryInitializationTimer = window.setInterval(() => {
      attempts += 1;

      const completed = openSecondProductMedia();

      if (
        completed ||
        attempts >= MAX_GALLERY_ATTEMPTS
      ) {
        window.clearInterval(
          galleryInitializationTimer
        );
      }
    }, GALLERY_RETRY_DELAY);
  };


    /* =========================================================
   RECHARGE — SYNC SELECTED PURCHASE PRICE TO PDP PRICE
   ========================================================= */

const rechargeWidgetObservers = new WeakMap();
let rechargePriceSyncTimer = null;

const getActiveRechargeWidget = () => {
  const widgets = Array.from(
    document.querySelectorAll(RECHARGE_WIDGET_SELECTOR)
  );

  return (
    widgets.find((widget) => {
      if (widget.hidden) return false;

      const styles = window.getComputedStyle(widget);

      return (
        styles.display !== 'none' &&
        styles.visibility !== 'hidden'
      );
    }) ||
    widgets[0] ||
    null
  );
};

const getSelectedRechargePurchaseMode = (widget) => {
  if (!widget?.shadowRoot) return null;

  const selectedOption = queryDeepAll(
    widget.shadowRoot,
    '[part~="rc-purchase-option__selected"]'
  )[0];

  const selectedParts =
    selectedOption?.getAttribute('part') || '';

  if (
    selectedParts.includes(
      'rc-purchase-option__subscription'
    )
  ) {
    return 'subscription';
  }

  if (
    selectedParts.includes(
      'rc-purchase-option__onetime'
    )
  ) {
    return 'onetime';
  }

  const selectedInput = queryDeepAll(
    widget.shadowRoot,
    'input[part~="rc-purchase-option__input"]:checked'
  )[0];

  if (selectedInput) {
    const selectedText = [
      selectedInput.value,
      selectedInput.id,
      selectedInput.name,
      selectedInput.getAttribute('aria-label'),
      selectedInput.parentElement?.textContent
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (/one[\s-]?time/.test(selectedText)) {
      return 'onetime';
    }

    if (/subscri|delivery/.test(selectedText)) {
      return 'subscription';
    }
  }

  const sellingPlanInput = document.querySelector(
    '.template-product form input[name="selling_plan"]'
  );

  if (sellingPlanInput) {
    return sellingPlanInput.value
      ? 'subscription'
      : 'onetime';
  }

  return null;
};

const getThemePriceHosts = () => {
  const finalPriceElements = Array.from(
    document.querySelectorAll(
      '.template-product .wt-product__main ' +
        '.wt-product__price__final, ' +
      '.template-product [id^="sticky-price-"] ' +
        '.wt-product__price__final'
    )
  );

  const hosts = finalPriceElements
    .map((element) => {
      let ancestor = element.parentElement;

      while (ancestor && ancestor !== document.body) {
        if (ancestor.classList.contains('wt-product__price')) {
          return ancestor;
        }

        ancestor = ancestor.parentElement;
      }

      return (
        element.closest('.price') ||
        element.parentElement
      );
    })
    .filter(Boolean);

  return Array.from(new Set(hosts));
};

const extractCleanMoney = (value) => {
  const normalized = String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!normalized) return null;

  /*
   * Pull only the actual dollar amount from text such as:
   * "Discounted price: $34.00"
   * "Regular price $40.00"
   */
  const dollarMatches =
    normalized.match(
      /\$\s*\d[\d,]*(?:\.\d{1,2})?/g
    ) || [];

  const numericMatches =
    normalized.match(
      /\d[\d,]*(?:\.\d{1,2})?/g
    ) || [];

  let priceText =
    dollarMatches[dollarMatches.length - 1] ||
    numericMatches[numericMatches.length - 1] ||
    '';

  priceText = priceText
    .replace(/\s+/g, '')
    .trim();

  const priceValue = parseMoney(priceText);

  if (
    !priceText ||
    priceValue === null ||
    priceValue <= 0
  ) {
    return null;
  }

  /*
   * The Good Patch storefront is USD.
   * Add the dollar sign if the fallback numeric match
   * did not contain one.
   */
  if (!priceText.startsWith('$')) {
    priceText = `$${priceValue.toFixed(2)}`;
  }

  return {
    text: priceText,
    value: priceValue
  };
};

const getRechargeSubscriptionPrice = (widget) => {
  if (!widget?.shadowRoot) return null;

  const discountedPriceElement = queryDeepAll(
    widget.shadowRoot,
    '[part~="rc-purchase-option__discounted-price"]'
  )[0];

  const originalPriceElement = queryDeepAll(
    widget.shadowRoot,
    '[part~="rc-purchase-option__original-price"]'
  )[0];

  const priceElement =
    discountedPriceElement ||
    originalPriceElement;

  return extractCleanMoney(
    priceElement?.textContent
  );
};

const getThemePriceSourceData = (priceHost) => {
  const finalPriceElement =
    priceHost.querySelector(
      '.price__sale .wt-product__price__final'
    ) ||
    priceHost.querySelector(
      '.price__regular .wt-product__price__final'
    ) ||
    priceHost.querySelector(
      '.wt-product__price__final'
    );

  const compareAtElement =
    priceHost.querySelector(
      '.wt-product__price__compare'
    );

  const oneTimePrice =
    extractCleanMoney(
      finalPriceElement?.textContent
    );

  const compareAtPrice =
    extractCleanMoney(
      compareAtElement?.textContent
    );

  if (!oneTimePrice) {
    return null;
  }

  return {
    oneTimePriceText: oneTimePrice.text,
    oneTimePriceValue: oneTimePrice.value,
    compareAtPriceText:
      compareAtPrice?.text || '',
    compareAtPriceValue:
      compareAtPrice?.value ?? null
  };
};

const getSyncedPriceRow = (priceHost) => {
  const existingRow =
    priceHost.nextElementSibling;

  if (
    existingRow?.classList.contains(
      'tgp-synced-price-row'
    )
  ) {
    return existingRow;
  }

  const row = document.createElement('div');

  row.className = 'tgp-synced-price-row';
  row.setAttribute('role', 'status');
  row.setAttribute('aria-live', 'polite');
  row.setAttribute('aria-atomic', 'true');

  priceHost.insertAdjacentElement(
    'afterend',
    row
  );

  return row;
};

const renderSyncedPriceRow = ({
  priceHost,
  mode,
  finalPriceText,
  finalPriceValue,
  compareAtPriceText,
  compareAtPriceValue
}) => {
  const row = getSyncedPriceRow(priceHost);

  const validCompareAt =
    compareAtPriceValue !== null &&
    compareAtPriceValue > finalPriceValue;

  const savingsPercentage = validCompareAt
    ? Math.round(
        (
          (
            compareAtPriceValue -
            finalPriceValue
          ) /
          compareAtPriceValue
        ) * 100
      )
    : 0;

  const purchaseLabel =
    mode === 'subscription'
      ? 'Subscription price'
      : 'One-time purchase price';

  const accessibleText = validCompareAt
    ? `${purchaseLabel} ${finalPriceText}. ` +
      `Original price ${compareAtPriceText}. ` +
      `Save ${savingsPercentage} percent.`
    : `${purchaseLabel} ${finalPriceText}.`;

  const screenReaderText =
    document.createElement('span');

  screenReaderText.className =
    'tgp-synced-price-row__sr-only';

  screenReaderText.textContent = accessibleText;

  const visibleFragment =
    document.createDocumentFragment();

  if (validCompareAt) {
    const compareAt = document.createElement('s');

    compareAt.className =
      'tgp-synced-price-row__compare';

    compareAt.textContent = compareAtPriceText;
    compareAt.setAttribute('aria-hidden', 'true');

    visibleFragment.appendChild(compareAt);
  }

  const finalPrice = document.createElement('span');

  finalPrice.className =
    'tgp-synced-price-row__final';

  finalPrice.textContent = finalPriceText;
  finalPrice.setAttribute('aria-hidden', 'true');

  visibleFragment.appendChild(finalPrice);

  if (validCompareAt) {
    const savings = document.createElement('span');

    savings.className =
      'tgp-synced-price-row__savings';

    savings.textContent = `-${savingsPercentage}%`;
    savings.setAttribute('aria-hidden', 'true');

    visibleFragment.appendChild(savings);
  }

  row.replaceChildren(
    screenReaderText,
    visibleFragment
  );

  row.dataset.tgpPriceMode = mode;
  row.dataset.tgpPriceValue =
    String(finalPriceValue);

  priceHost.classList.add(
    'tgp-theme-price--hidden'
  );

  priceHost.setAttribute('aria-hidden', 'true');
};

const restoreThemePrices = () => {
  document
    .querySelectorAll(
      '.template-product .tgp-theme-price--hidden'
    )
    .forEach((priceHost) => {
      priceHost.classList.remove(
        'tgp-theme-price--hidden'
      );

      priceHost.removeAttribute('aria-hidden');
    });

  document
    .querySelectorAll(
      '.template-product .tgp-synced-price-row'
    )
    .forEach((row) => row.remove());
};


/* =========================================================
   RECHARGE VARIATION B — GLOBAL DYNAMIC WIDGET PRICING
   Applies only when the widget badge reads "SUBSCRIBE & SAVE:".
   Uses the selected variant's Shopify price and compare-at price.
   ========================================================= */

const VARIATION_A_BADGE_TEXT = 'best value';
const VARIATION_B_BADGE_TEXT = 'subscribe & save:';

const normalizeWidgetText = (value) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const getPartElement = (root, partName) =>
  queryDeepAll(
    root,
    `[part~="${partName}"]`
  )[0] || null;

const addPartToken = (element, token) => {
  if (!element) return;

  const currentTokens = String(
    element.getAttribute('part') || ''
  )
    .split(/\s+/)
    .filter(Boolean);

  if (currentTokens.includes(token)) {
    return;
  }

  element.setAttribute(
    'part',
    [...currentTokens, token].join(' ')
  );
};

const removePartToken = (element, token) => {
  if (!element) return;

  const currentTokens = String(
    element.getAttribute('part') || ''
  )
    .split(/\s+/)
    .filter(Boolean);

  if (!currentTokens.includes(token)) {
    return;
  }

  const updatedTokens = currentTokens.filter(
    (currentToken) => currentToken !== token
  );

  if (updatedTokens.length) {
    element.setAttribute(
      'part',
      updatedTokens.join(' ')
    );
  } else {
    element.removeAttribute('part');
  }
};

const getRechargeWidgetBadgeText = (widget) => {
  if (!widget?.shadowRoot) return '';

  const badge = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__badge'
  );

  if (!badge) return '';

  /*
   * Variation A has a dynamic mobile savings badge inside
   * the native BEST VALUE bar. Exclude that injected child
   * before identifying the active Recharge test variation.
   */
  const badgeClone = badge.cloneNode(true);

  badgeClone
    .querySelectorAll(
      '[part~="tgp-recharge-a-save-badge-mobile"]'
    )
    .forEach((element) => element.remove());

  return normalizeWidgetText(
    badgeClone.textContent
  );
};

const isVariationARechargeWidget = (widget) =>
  getRechargeWidgetBadgeText(widget) ===
  VARIATION_A_BADGE_TEXT;

const isVariationBRechargeWidget = (widget) =>
  getRechargeWidgetBadgeText(widget) ===
  VARIATION_B_BADGE_TEXT;

const getPrimaryThemePriceSourceData = () => {
  const priceHosts = getThemePriceHosts();

  if (!priceHosts.length) return null;

  const mainPriceHost =
    priceHosts.find(
      (priceHost) =>
        !String(priceHost.id || '').startsWith(
          'sticky-price-'
        )
    ) || priceHosts[0];

  return getThemePriceSourceData(mainPriceHost);
};

/* =========================================================
   RECHARGE VARIATION A — SAFE DYNAMIC PRICING ENHANCEMENT

   Important rules:
   - Recharge remains the source of truth for its live current
     subscription and one-time prices.
   - This code never rewrites or restores Recharge's native
     price text, so native variant switching remains intact.
   - Shopify supplies the selected variant's compare-at price.
   - Enhancements are applied only after Recharge's native
     one-time price matches Shopify's selected variant price.
   ========================================================= */

const VARIATION_A_INJECTED_SELECTOR = [
  '[part~="tgp-recharge-a-subscription-compare"]',
  '[part~="tgp-recharge-a-save-badge-desktop"]',
  '[part~="tgp-recharge-a-save-badge-mobile"]',
  '[part~="tgp-recharge-a-onetime-prices"]'
].join(', ');

const pricesAreEqual = (left, right) =>
  Number.isFinite(left) &&
  Number.isFinite(right) &&
  Math.abs(left - right) < 0.01;

const setAttributeIfChanged = (
  element,
  name,
  value
) => {
  if (!element) return;

  if (element.getAttribute(name) !== value) {
    element.setAttribute(name, value);
  }
};

const getRechargeOneTimePrice = (widget) => {
  if (!widget?.shadowRoot) return null;

  const oneTimeOption = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__onetime'
  );

  const priceElement = oneTimeOption
    ? getPartElement(
        oneTimeOption,
        'rc-purchase-option__price'
      )
    : getPartElement(
        widget.shadowRoot,
        'rc-purchase-option__price'
      );

  return extractCleanMoney(
    priceElement?.textContent
  );
};

const getOrCreateVariationAElement = ({
  root,
  partName,
  tagName,
  parent
}) => {
  let element = getPartElement(
    root,
    partName
  );

  if (!element) {
    element = document.createElement(tagName);
    element.setAttribute('part', partName);
  }

  if (
    parent &&
    element.parentElement !== parent
  ) {
    parent.appendChild(element);
  }

  return element;
};

const removeVariationAInjectedElements = (widget) => {
  if (!widget?.shadowRoot) return;

  queryDeepAll(
    widget.shadowRoot,
    VARIATION_A_INJECTED_SELECTOR
  ).forEach((element) => element.remove());

  const originalSubscriptionPrice = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__original-price'
  );

  removePartToken(
    originalSubscriptionPrice,
    'tgp-recharge-a-native-original-hidden'
  );

  if (
    originalSubscriptionPrice?.getAttribute(
      'aria-hidden'
    ) === 'true'
  ) {
    originalSubscriptionPrice.removeAttribute(
      'aria-hidden'
    );
  }

  const subscriptionOption = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__subscription'
  );

  const subscriptionLabel = subscriptionOption
    ? getPartElement(
        subscriptionOption,
        'rc-purchase-option__label'
      )
    : null;

  removePartToken(
    subscriptionLabel,
    'tgp-recharge-a-subscription-label'
  );

  const oneTimeOption = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__onetime'
  );

  const oneTimeLabel = oneTimeOption
    ? getPartElement(
        oneTimeOption,
        'rc-purchase-option__label'
      )
    : null;

  const nativeOneTimePrice = oneTimeOption
    ? getPartElement(
        oneTimeOption,
        'rc-purchase-option__price'
      )
    : null;

  removePartToken(
    oneTimeLabel,
    'tgp-recharge-a-onetime-label'
  );

  removePartToken(
    nativeOneTimePrice,
    'tgp-recharge-a-native-onetime-hidden'
  );

  if (
    nativeOneTimePrice?.getAttribute(
      'aria-hidden'
    ) === 'true'
  ) {
    nativeOneTimePrice.removeAttribute(
      'aria-hidden'
    );
  }

  widget.classList.remove(
    'tgp-recharge-variation-a'
  );
};

const syncVariationARechargePricing = (widget) => {
  if (!widget?.shadowRoot) return;

  if (!isVariationARechargeWidget(widget)) {
    removeVariationAInjectedElements(widget);
    return;
  }

  const sourceData =
    getPrimaryThemePriceSourceData();

  const subscriptionPrice =
    getRechargeSubscriptionPrice(widget);

  const rechargeOneTimePrice =
    getRechargeOneTimePrice(widget);

  /*
   * This gate is the key variant-safety check. During a variant
   * transition, Shopify can update before Recharge. Do not add
   * comparison prices or badges until both systems agree on the
   * selected variant's native one-time price.
   */
  const rechargeMatchesSelectedVariant =
    sourceData &&
    subscriptionPrice &&
    rechargeOneTimePrice &&
    pricesAreEqual(
      rechargeOneTimePrice.value,
      sourceData.oneTimePriceValue
    );

  if (!rechargeMatchesSelectedVariant) {
    removeVariationAInjectedElements(widget);
    return;
  }

  const priceRow = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__prices'
  );

  const headerBadge = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__badge'
  );

  const originalSubscriptionPrice = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__original-price'
  );

  const subscriptionOption = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__subscription'
  );

  const subscriptionLabel = subscriptionOption
    ? getPartElement(
        subscriptionOption,
        'rc-purchase-option__label'
      )
    : null;

  const oneTimeOption = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__onetime'
  );

  const oneTimeLabel = oneTimeOption
    ? getPartElement(
        oneTimeOption,
        'rc-purchase-option__label'
      )
    : null;

  const nativeOneTimePrice = oneTimeOption
    ? getPartElement(
        oneTimeOption,
        'rc-purchase-option__price'
      )
    : null;

  if (
    !priceRow ||
    !headerBadge ||
    !originalSubscriptionPrice ||
    !subscriptionLabel ||
    !oneTimeOption ||
    !oneTimeLabel ||
    !nativeOneTimePrice
  ) {
    removeVariationAInjectedElements(widget);
    return;
  }

  widget.classList.add(
    'tgp-recharge-variation-a'
  );

  addPartToken(
    subscriptionLabel,
    'tgp-recharge-a-subscription-label'
  );

  addPartToken(
    oneTimeLabel,
    'tgp-recharge-a-onetime-label'
  );

  /*
   * Subscription comparison priority:
   * 1. Shopify compare-at price when it is above the live
   *    Recharge subscription price.
   * 2. Otherwise the selected variant's one-time price.
   */
  const shopifyCompareAtIsValid =
    sourceData.compareAtPriceValue !== null &&
    sourceData.compareAtPriceValue >
      subscriptionPrice.value;

  const oneTimeIsValidSubscriptionCompare =
    sourceData.oneTimePriceValue >
      subscriptionPrice.value;

  const subscriptionCompareText =
    shopifyCompareAtIsValid
      ? sourceData.compareAtPriceText
      : oneTimeIsValidSubscriptionCompare
        ? sourceData.oneTimePriceText
        : '';

  const subscriptionCompareValue =
    shopifyCompareAtIsValid
      ? sourceData.compareAtPriceValue
      : oneTimeIsValidSubscriptionCompare
        ? sourceData.oneTimePriceValue
        : null;

  if (
    subscriptionCompareText &&
    subscriptionCompareValue !== null
  ) {
    addPartToken(
      originalSubscriptionPrice,
      'tgp-recharge-a-native-original-hidden'
    );

    setAttributeIfChanged(
      originalSubscriptionPrice,
      'aria-hidden',
      'true'
    );

    const subscriptionCompare =
      getOrCreateVariationAElement({
        root: widget.shadowRoot,
        partName:
          'tgp-recharge-a-subscription-compare',
        tagName: 's',
        parent: priceRow
      });

    if (
      subscriptionCompare.textContent !==
      subscriptionCompareText
    ) {
      subscriptionCompare.textContent =
        subscriptionCompareText;
    }

    setAttributeIfChanged(
      subscriptionCompare,
      'aria-label',
      `Original price ${subscriptionCompareText}`
    );

    const savingsPercentage = Math.round(
      (
        (
          subscriptionCompareValue -
          subscriptionPrice.value
        ) /
        subscriptionCompareValue
      ) * 100
    );

    const savingsText =
      `SAVE ${savingsPercentage}%`;

    const desktopSavingsBadge =
      getOrCreateVariationAElement({
        root: widget.shadowRoot,
        partName:
          'tgp-recharge-a-save-badge-desktop',
        tagName: 'span',
        parent: priceRow
      });

    const mobileSavingsBadge =
      getOrCreateVariationAElement({
        root: widget.shadowRoot,
        partName:
          'tgp-recharge-a-save-badge-mobile',
        tagName: 'span',
        parent: headerBadge
      });

    [
      desktopSavingsBadge,
      mobileSavingsBadge
    ].forEach((savingsBadge) => {
      if (
        savingsBadge.textContent !==
        savingsText
      ) {
        savingsBadge.textContent =
          savingsText;
      }

      setAttributeIfChanged(
        savingsBadge,
        'aria-label',
        `Save ${savingsPercentage} percent`
      );
    });
  } else {
    queryDeepAll(
      widget.shadowRoot,
      [
        '[part~="tgp-recharge-a-subscription-compare"]',
        '[part~="tgp-recharge-a-save-badge-desktop"]',
        '[part~="tgp-recharge-a-save-badge-mobile"]'
      ].join(', ')
    ).forEach((element) => element.remove());

    removePartToken(
      originalSubscriptionPrice,
      'tgp-recharge-a-native-original-hidden'
    );

    if (
      originalSubscriptionPrice?.getAttribute(
        'aria-hidden'
      ) === 'true'
    ) {
      originalSubscriptionPrice.removeAttribute(
        'aria-hidden'
      );
    }
  }

  /*
   * Present the one-time price in a dedicated flex group.
   *
   * Recharge still owns the real current price and variant
   * switching. The visible current-price span only mirrors
   * Recharge's already-confirmed native value. This avoids
   * positioning Recharge's internal price element while also
   * preventing stale Shopify pricing from being injected.
   */
  const oneTimePriceGroup =
    getOrCreateVariationAElement({
      root: widget.shadowRoot,
      partName:
        'tgp-recharge-a-onetime-prices',
      tagName: 'span',
      parent: oneTimeLabel
    });

  const displayedOneTimePrice =
    getOrCreateVariationAElement({
      root: widget.shadowRoot,
      partName:
        'tgp-recharge-a-onetime-current',
      tagName: 'span',
      parent: oneTimePriceGroup
    });

  if (
    displayedOneTimePrice.textContent !==
    rechargeOneTimePrice.text
  ) {
    displayedOneTimePrice.textContent =
      rechargeOneTimePrice.text;
  }

  setAttributeIfChanged(
    displayedOneTimePrice,
    'aria-hidden',
    'true'
  );

  addPartToken(
    nativeOneTimePrice,
    'tgp-recharge-a-native-onetime-hidden'
  );

  setAttributeIfChanged(
    nativeOneTimePrice,
    'aria-hidden',
    'true'
  );

  const validOneTimeCompareAt =
    sourceData.compareAtPriceValue !== null &&
    sourceData.compareAtPriceValue >
      rechargeOneTimePrice.value;

  if (validOneTimeCompareAt) {
    const oneTimeCompare =
      getOrCreateVariationAElement({
        root: widget.shadowRoot,
        partName:
          'tgp-recharge-a-onetime-compare',
        tagName: 's',
        parent: oneTimePriceGroup
      });

    if (
      oneTimeCompare.textContent !==
      sourceData.compareAtPriceText
    ) {
      oneTimeCompare.textContent =
        sourceData.compareAtPriceText;
    }

    setAttributeIfChanged(
      oneTimeCompare,
      'aria-hidden',
      'true'
    );

    setAttributeIfChanged(
      oneTimePriceGroup,
      'aria-label',
      `One-time price ${rechargeOneTimePrice.text}. ` +
        `Original price ${sourceData.compareAtPriceText}.`
    );
  } else {
    queryDeepAll(
      oneTimePriceGroup,
      '[part~="tgp-recharge-a-onetime-compare"]'
    ).forEach((element) => element.remove());

    setAttributeIfChanged(
      oneTimePriceGroup,
      'aria-label',
      `One-time price ${rechargeOneTimePrice.text}.`
    );
  }
};


const removeVariationBInjectedElements = (widget) => {
  if (!widget?.shadowRoot) return;

  queryDeepAll(
    widget.shadowRoot,
    [
  '[part~="tgp-recharge-subscription-compare"]',
  '[part~="tgp-recharge-save-badge"]',
  '[part~="tgp-recharge-onetime-prices"]',
  '[part~="tgp-recharge-subscription-per-patch"]',
  '[part~="tgp-recharge-onetime-per-patch"]'
].join(', ')
  ).forEach((element) => element.remove());

  const originalSubscriptionPrice = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__original-price'
  );

  originalSubscriptionPrice?.removeAttribute(
    'aria-hidden'
  );

  const defaultOneTimePrice = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__price'
  );

  defaultOneTimePrice?.removeAttribute(
    'aria-hidden'
  );

  const oneTimeOption = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__onetime'
  );

  const oneTimeLabel = oneTimeOption
    ? getPartElement(
        oneTimeOption,
        'rc-purchase-option__label'
      )
    : null;

  removePartToken(
    oneTimeLabel,
    'tgp-recharge-onetime-label'
  );

  widget.classList.remove(
    'tgp-recharge-variation-b'
  );
};

const getOrCreateVariationBElement = ({
  root,
  selector,
  tagName,
  partName,
  parent
}) => {
  let element = queryDeepAll(
    root,
    selector
  )[0];

  if (!element) {
    element = document.createElement(tagName);
    element.setAttribute('part', partName);
    parent.appendChild(element);
  }

  return element;
};

/* =========================================================
   VARIATION B — DYNAMIC PER-PATCH PRICING
   Reads patch quantity from the selected Shopify variant.
   Nothing is hardcoded to 4, 28, or any specific product.
   ========================================================= */

const getSelectedPatchCount = () => {
  const selectedValues = [];

  document
    .querySelectorAll(
      '.template-product .wt-product__option input[type="radio"]:checked'
    )
    .forEach((input) => {
      selectedValues.push(input.value);
    });

  document
    .querySelectorAll(
      '.template-product .wt-product__option select option:checked'
    )
    .forEach((option) => {
      selectedValues.push(
        option.value || option.textContent
      );
    });

  document
    .querySelectorAll(
      '.template-product .wt-product__option__title .value'
    )
    .forEach((element) => {
      selectedValues.push(element.textContent);
    });

  for (const value of selectedValues) {
    const match = String(value || '').match(
      /(\d+(?:\.\d+)?)\s*patch(?:es)?\b/i
    );

    if (!match) continue;

    const patchCount = Number(match[1]);

    if (
      Number.isFinite(patchCount) &&
      patchCount > 0
    ) {
      return patchCount;
    }
  }

  return null;
};

const formatPerPatchPrice = (
  totalPrice,
  patchCount
) => {
  if (
    !Number.isFinite(totalPrice) ||
    !Number.isFinite(patchCount) ||
    patchCount <= 0
  ) {
    return '';
  }

  return `$${(
    totalPrice / patchCount
  ).toFixed(2)} per patch`;
};

const removeVariationBPerPatchElements = (
  widget
) => {
  if (!widget?.shadowRoot) return;

  queryDeepAll(
    widget.shadowRoot,
    [
      '[part~="tgp-recharge-subscription-per-patch"]',
      '[part~="tgp-recharge-onetime-per-patch"]'
    ].join(', ')
  ).forEach((element) => element.remove());
};

const syncVariationBPerPatchPricing = ({
  widget,
  sourceData,
  subscriptionPrice,
  priceRow,
  priceGroup
}) => {
  if (
    !widget?.shadowRoot ||
    !sourceData ||
    !subscriptionPrice
  ) {
    removeVariationBPerPatchElements(widget);
    return;
  }

  const patchCount = getSelectedPatchCount();
  const rechargeOneTimePrice =
    getRechargeOneTimePrice(widget);

  /*
   * Shopify can update before Recharge during a variant switch.
   * Only show the unit prices once Recharge's one-time price
   * matches Shopify's selected variant price.
   */
  const variantPricesAreSynced =
    rechargeOneTimePrice &&
    pricesAreEqual(
      rechargeOneTimePrice.value,
      sourceData.oneTimePriceValue
    );

  if (!patchCount || !variantPricesAreSynced) {
    removeVariationBPerPatchElements(widget);
    return;
  }

  if (priceRow) {
    const subscriptionPerPatchValue =
      (
        subscriptionPrice.value /
        patchCount
      ).toFixed(2);

    const subscriptionPerPatch =
      getOrCreateVariationBElement({
        root: widget.shadowRoot,
        selector:
          '[part~="tgp-recharge-subscription-per-patch"]',
        tagName: 'span',
        partName:
          'tgp-recharge-subscription-per-patch',
        parent: priceRow
      });

    subscriptionPerPatch.innerHTML = `
      <span part="tgp-recharge-per-patch-price">
        <span part="tgp-recharge-per-patch-amount">$${subscriptionPerPatchValue}</span>
        <span part="tgp-recharge-per-patch-suffix">/ patch</span>
      </span>

      <span
        part="tgp-recharge-per-patch-divider"
        aria-hidden="true"
      ></span>

      <span part="tgp-recharge-per-patch-value">
        <svg
          part="tgp-recharge-per-patch-tag"
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20.59 13.41 11 23l-9-9V2h12l6.59 6.59a2 2 0 0 1 0 2.82Z"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <circle
            cx="7"
            cy="7"
            r="1.5"
            stroke="currentColor"
            stroke-width="1.8"
          />
        </svg>
        <span part="tgp-recharge-per-patch-message">lowest per-patch price</span>
      </span>
    `;

    /*
     * Recharge can render the price row inside different nested shadow
     * structures on desktop vs mobile. Styling these injected elements
     * only through the Recharge stylesheet is therefore not reliable on
     * every mobile browser. Apply the critical callout styling inline and
     * choose mobile values from the live viewport. This keeps the layout
     * identical after Recharge rerenders or a customer changes variants.
     */
    const forceStyle = (element, property, value) => {
      element?.style?.setProperty(
        property,
        value,
        'important'
      );
    };

    const isMobileViewport = window.matchMedia(
      '(max-width: 749px)'
    ).matches;

    const isSmallMobileViewport = window.matchMedia(
      '(max-width: 389px)'
    ).matches;

    const perPatchPrice = getPartElement(
      subscriptionPerPatch,
      'tgp-recharge-per-patch-price'
    );

    const perPatchAmount = getPartElement(
      subscriptionPerPatch,
      'tgp-recharge-per-patch-amount'
    );

    const perPatchSuffix = getPartElement(
      subscriptionPerPatch,
      'tgp-recharge-per-patch-suffix'
    );

    const perPatchDivider = getPartElement(
      subscriptionPerPatch,
      'tgp-recharge-per-patch-divider'
    );

    const perPatchValue = getPartElement(
      subscriptionPerPatch,
      'tgp-recharge-per-patch-value'
    );

    const perPatchTag = getPartElement(
      subscriptionPerPatch,
      'tgp-recharge-per-patch-tag'
    );

    const perPatchMessage = getPartElement(
      subscriptionPerPatch,
      'tgp-recharge-per-patch-message'
    );

    /* Price area: first row = subscription prices, second row = callout. */
    forceStyle(priceRow, 'display', 'grid');
    forceStyle(
      priceRow,
      'grid-template-columns',
      'max-content max-content max-content'
    );
    forceStyle(priceRow, 'align-items', 'center');
    forceStyle(priceRow, 'justify-content', 'start');
    forceStyle(
      priceRow,
      'column-gap',
      isMobileViewport ? '9px' : '12px'
    );
    forceStyle(
      priceRow,
      'row-gap',
      isMobileViewport ? '8px' : '10px'
    );

    /* Outer premium callout. */
    forceStyle(subscriptionPerPatch, 'display', 'inline-grid');
    forceStyle(
      subscriptionPerPatch,
      'grid-template-columns',
      'max-content 1px max-content'
    );
    forceStyle(subscriptionPerPatch, 'align-items', 'center');
    forceStyle(subscriptionPerPatch, 'grid-column', '1 / -1');
    forceStyle(subscriptionPerPatch, 'justify-self', 'start');
    forceStyle(subscriptionPerPatch, 'width', 'max-content');
    forceStyle(subscriptionPerPatch, 'max-width', '100%');
    forceStyle(subscriptionPerPatch, 'min-width', '0');
    forceStyle(
      subscriptionPerPatch,
      'min-height',
      isMobileViewport ? '38px' : '44px'
    );
    forceStyle(subscriptionPerPatch, 'margin', '0');
    forceStyle(
      subscriptionPerPatch,
      'padding',
      isMobileViewport
        ? (isSmallMobileViewport ? '7px 9px' : '8px 11px')
        : '9px 16px'
    );
    forceStyle(
      subscriptionPerPatch,
      'column-gap',
      isMobileViewport ? '8px' : '14px'
    );
    forceStyle(
      subscriptionPerPatch,
      'border',
      '1px solid rgba(160, 38, 120, .16)'
    );
    forceStyle(
      subscriptionPerPatch,
      'border-radius',
      isMobileViewport ? '10px' : '12px'
    );
    forceStyle(
      subscriptionPerPatch,
      'background',
      'rgba(255, 255, 255, .26)'
    );
    forceStyle(subscriptionPerPatch, 'overflow', 'hidden');
    forceStyle(subscriptionPerPatch, 'box-sizing', 'border-box');

    /* $1.65 / patch */
    forceStyle(perPatchPrice, 'display', 'inline-flex');
    forceStyle(perPatchPrice, 'align-items', 'baseline');
    forceStyle(perPatchPrice, 'gap', isMobileViewport ? '4px' : '5px');
    forceStyle(perPatchPrice, 'white-space', 'nowrap');
    forceStyle(perPatchPrice, 'margin', '0');
    forceStyle(perPatchPrice, 'padding', '0');

    forceStyle(perPatchAmount, 'color', '#A02678');
    forceStyle(perPatchAmount, 'font-family', 'inherit');
    forceStyle(
      perPatchAmount,
      'font-size',
      isMobileViewport
        ? (isSmallMobileViewport ? '16px' : '17px')
        : '21px'
    );
    forceStyle(perPatchAmount, 'font-weight', '800');
    forceStyle(perPatchAmount, 'line-height', '1');
    forceStyle(perPatchAmount, 'white-space', 'nowrap');

    forceStyle(perPatchSuffix, 'color', '#2A234B');
    forceStyle(perPatchSuffix, 'font-family', 'inherit');
    forceStyle(
      perPatchSuffix,
      'font-size',
      isMobileViewport
        ? (isSmallMobileViewport ? '9px' : '10px')
        : '13px'
    );
    forceStyle(perPatchSuffix, 'font-weight', '500');
    forceStyle(perPatchSuffix, 'line-height', '1');
    forceStyle(perPatchSuffix, 'white-space', 'nowrap');

    /* Divider */
    forceStyle(perPatchDivider, 'display', 'block');
    forceStyle(perPatchDivider, 'width', '1px');
    forceStyle(perPatchDivider, 'min-width', '1px');
    forceStyle(
      perPatchDivider,
      'height',
      isMobileViewport ? '18px' : '24px'
    );
    forceStyle(
      perPatchDivider,
      'background',
      'rgba(160, 38, 120, .18)'
    );
    forceStyle(perPatchDivider, 'margin', '0');
    forceStyle(perPatchDivider, 'padding', '0');

    /* Right-side value message. */
    forceStyle(perPatchValue, 'display', 'inline-flex');
    forceStyle(perPatchValue, 'align-items', 'center');
    forceStyle(perPatchValue, 'gap', isMobileViewport ? '4px' : '7px');
    forceStyle(perPatchValue, 'min-width', '0');
    forceStyle(perPatchValue, 'white-space', 'nowrap');
    forceStyle(perPatchValue, 'color', '#2A234B');
    forceStyle(perPatchValue, 'margin', '0');
    forceStyle(perPatchValue, 'padding', '0');

    forceStyle(perPatchTag, 'display', 'block');
    forceStyle(
      perPatchTag,
      'width',
      isMobileViewport ? '11px' : '15px'
    );
    forceStyle(
      perPatchTag,
      'height',
      isMobileViewport ? '11px' : '15px'
    );
    forceStyle(
      perPatchTag,
      'min-width',
      isMobileViewport ? '11px' : '15px'
    );
    forceStyle(perPatchTag, 'color', '#A02678');
    forceStyle(perPatchTag, 'overflow', 'visible');

    forceStyle(perPatchMessage, 'color', '#2A234B');
    forceStyle(perPatchMessage, 'font-family', 'inherit');
    forceStyle(
      perPatchMessage,
      'font-size',
      isMobileViewport
        ? (isSmallMobileViewport ? '8px' : '9px')
        : '12px'
    );
    forceStyle(perPatchMessage, 'font-weight', '500');
    forceStyle(perPatchMessage, 'line-height', '1.05');
    forceStyle(perPatchMessage, 'white-space', 'nowrap');

    subscriptionPerPatch.setAttribute(
      'aria-label',
      `$${subscriptionPerPatchValue} per patch. Lowest per-patch price.`
    );
  }

  if (priceGroup) {
    const oneTimePerPatchText =
      formatPerPatchPrice(
        sourceData.oneTimePriceValue,
        patchCount
      );

    const oneTimePerPatch =
      getOrCreateVariationBElement({
        root: widget.shadowRoot,
        selector:
          '[part~="tgp-recharge-onetime-per-patch"]',
        tagName: 'span',
        partName:
          'tgp-recharge-onetime-per-patch',
        parent: priceGroup
      });

    if (
      oneTimePerPatch.textContent !==
      oneTimePerPatchText
    ) {
      oneTimePerPatch.textContent =
        oneTimePerPatchText;
    }

    oneTimePerPatch.setAttribute(
      'aria-hidden',
      'true'
    );

    const existingAriaLabel =
      priceGroup.getAttribute('aria-label') || '';

    priceGroup.setAttribute(
      'aria-label',
      `${existingAriaLabel} ${oneTimePerPatchText}.`.trim()
    );
  }
};

const syncVariationBRechargePricing = (widget) => {
  if (!widget?.shadowRoot) return;

  if (!isVariationBRechargeWidget(widget)) {
    removeVariationBInjectedElements(widget);
    return;
  }

  const sourceData =
    getPrimaryThemePriceSourceData();

  const subscriptionPrice =
    getRechargeSubscriptionPrice(widget);

  if (!sourceData || !subscriptionPrice) {
    return;
  }

  widget.classList.add(
    'tgp-recharge-variation-b'
  );

  const priceRow = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__prices'
  );

  const originalSubscriptionPrice = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__original-price'
  );

  if (priceRow) {
    originalSubscriptionPrice?.setAttribute(
      'aria-hidden',
      'true'
    );

    const validSubscriptionCompareAt =
      sourceData.compareAtPriceValue !== null &&
      sourceData.compareAtPriceValue >
        subscriptionPrice.value;

    if (validSubscriptionCompareAt) {
      const compareAt =
        getOrCreateVariationBElement({
          root: widget.shadowRoot,
          selector:
            '[part~="tgp-recharge-subscription-compare"]',
          tagName: 's',
          partName:
            'tgp-recharge-subscription-compare',
          parent: priceRow
        });

      if (
        compareAt.textContent !==
        sourceData.compareAtPriceText
      ) {
        compareAt.textContent =
          sourceData.compareAtPriceText;
      }

      compareAt.setAttribute(
        'aria-label',
        `Original price ${sourceData.compareAtPriceText}`
      );

      const savingsPercentage = Math.round(
        (
          (
            sourceData.compareAtPriceValue -
            subscriptionPrice.value
          ) /
          sourceData.compareAtPriceValue
        ) * 100
      );

      const savingsBadge =
        getOrCreateVariationBElement({
          root: widget.shadowRoot,
          selector:
            '[part~="tgp-recharge-save-badge"]',
          tagName: 'span',
          partName:
            'tgp-recharge-save-badge',
          parent: priceRow
        });

      const savingsText =
        `SAVE ${savingsPercentage}%`;

      if (
        savingsBadge.textContent !==
        savingsText
      ) {
        savingsBadge.textContent =
          savingsText;
      }
    } else {
      queryDeepAll(
        widget.shadowRoot,
        [
          '[part~="tgp-recharge-subscription-compare"]',
          '[part~="tgp-recharge-save-badge"]'
        ].join(', ')
      ).forEach((element) => element.remove());
    }
  }

  const oneTimeOption = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__onetime'
  );

  if (!oneTimeOption) return;

  const oneTimeLabel = getPartElement(
    oneTimeOption,
    'rc-purchase-option__label'
  );

  addPartToken(
    oneTimeLabel,
    'tgp-recharge-onetime-label'
  );

  const defaultOneTimePrice = getPartElement(
    widget.shadowRoot,
    'rc-purchase-option__price'
  );

  defaultOneTimePrice?.setAttribute(
    'aria-hidden',
    'true'
  );

  const priceGroup =
    getOrCreateVariationBElement({
      root: widget.shadowRoot,
      selector:
        '[part~="tgp-recharge-onetime-prices"]',
      tagName: 'span',
      partName:
        'tgp-recharge-onetime-prices',
      parent: oneTimeLabel || oneTimeOption
    });

  /*
   * Recharge nests its default one-time price inside the
   * real option label. Keep our dynamic current/compare pair
   * in that same label so the complete option remains one
   * horizontal row on every product and variant.
   */
  if (
    oneTimeLabel &&
    priceGroup.parentElement !== oneTimeLabel
  ) {
    oneTimeLabel.appendChild(priceGroup);
  }

  const currentOneTimePrice =
    getOrCreateVariationBElement({
      root: widget.shadowRoot,
      selector:
        '[part~="tgp-recharge-onetime-current"]',
      tagName: 'span',
      partName:
        'tgp-recharge-onetime-current',
      parent: priceGroup
    });

  if (
    currentOneTimePrice.textContent !==
    sourceData.oneTimePriceText
  ) {
    currentOneTimePrice.textContent =
      sourceData.oneTimePriceText;
  }

  const validOneTimeCompareAt =
    sourceData.compareAtPriceValue !== null &&
    sourceData.compareAtPriceValue >
      sourceData.oneTimePriceValue;

  if (validOneTimeCompareAt) {
    const oneTimeCompareAt =
      getOrCreateVariationBElement({
        root: widget.shadowRoot,
        selector:
          '[part~="tgp-recharge-onetime-compare"]',
        tagName: 's',
        partName:
          'tgp-recharge-onetime-compare',
        parent: priceGroup
      });

    if (
      oneTimeCompareAt.textContent !==
      sourceData.compareAtPriceText
    ) {
      oneTimeCompareAt.textContent =
        sourceData.compareAtPriceText;
    }

    priceGroup.setAttribute(
      'aria-label',
      `One-time price ${sourceData.oneTimePriceText}. ` +
        `Original price ${sourceData.compareAtPriceText}.`
    );
  } else {
    queryDeepAll(
      priceGroup,
      '[part~="tgp-recharge-onetime-compare"]'
    ).forEach((element) => element.remove());

    priceGroup.setAttribute(
      'aria-label',
      `One-time price ${sourceData.oneTimePriceText}.`
    );
  }

  syncVariationBPerPatchPricing({
    widget,
    sourceData,
    subscriptionPrice,
    priceRow,
    priceGroup
  });

  queryDeepAll(
    priceGroup,
    [
      '[part~="tgp-recharge-onetime-current"]',
      '[part~="tgp-recharge-onetime-compare"]'
    ].join(', ')
  ).forEach((element) => {
    element.setAttribute(
      'aria-hidden',
      'true'
    );
  });
};

const syncProductPriceWithRecharge = () => {
  if (
    !document.body.classList.contains(
      'template-product'
    )
  ) {
    return;
  }

  const widget = getActiveRechargeWidget();

  if (!widget?.shadowRoot) {
    restoreThemePrices();
    return;
  }

  if (isVariationARechargeWidget(widget)) {
    removeVariationBInjectedElements(widget);
    syncVariationARechargePricing(widget);
  } else if (isVariationBRechargeWidget(widget)) {
    removeVariationAInjectedElements(widget);
    syncVariationBRechargePricing(widget);
  } else {
    removeVariationAInjectedElements(widget);
    removeVariationBInjectedElements(widget);
  }

  const purchaseMode =
    getSelectedRechargePurchaseMode(widget);

  const priceHosts = getThemePriceHosts();

  if (!priceHosts.length) return;

  const subscriptionPrice =
    purchaseMode === 'subscription'
      ? getRechargeSubscriptionPrice(widget)
      : null;

  if (
    purchaseMode === 'subscription' &&
    !subscriptionPrice
  ) {
    restoreThemePrices();
    return;
  }

  priceHosts.forEach((priceHost) => {
    const sourceData =
      getThemePriceSourceData(priceHost);

    if (!sourceData) return;

    if (purchaseMode === 'subscription') {
      renderSyncedPriceRow({
        priceHost,
        mode: 'subscription',
        finalPriceText: subscriptionPrice.text,
        finalPriceValue: subscriptionPrice.value,
        compareAtPriceText:
          sourceData.compareAtPriceText,
        compareAtPriceValue:
          sourceData.compareAtPriceValue
      });

      return;
    }

    renderSyncedPriceRow({
      priceHost,
      mode: 'onetime',
      finalPriceText:
        sourceData.oneTimePriceText,
      finalPriceValue:
        sourceData.oneTimePriceValue,
      compareAtPriceText:
        sourceData.compareAtPriceText,
      compareAtPriceValue:
        sourceData.compareAtPriceValue
    });
  });
};

const scheduleRechargePriceSync = (
  delay = 80
) => {
  window.clearTimeout(rechargePriceSyncTimer);

  rechargePriceSyncTimer =
    window.setTimeout(() => {
      observeRechargePriceWidgets();
      syncProductPriceWithRecharge();
    }, delay);
};

const observeRechargePriceWidget = (widget) => {
  if (!widget?.shadowRoot) return;

  const existing =
    rechargeWidgetObservers.get(widget);

  if (existing?.root === widget.shadowRoot) {
    return;
  }

  existing?.observer?.disconnect();

  const observer = new MutationObserver(() => {
    scheduleRechargePriceSync(40);
  });

  observer.observe(widget.shadowRoot, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: [
      'part',
      'checked',
      'aria-checked',
      'class',
      'value'
    ]
  });

  widget.shadowRoot.addEventListener(
    'change',
    () => scheduleRechargePriceSync(20),
    true
  );

  widget.shadowRoot.addEventListener(
    'click',
    () => scheduleRechargePriceSync(40),
    true
  );

  rechargeWidgetObservers.set(widget, {
    root: widget.shadowRoot,
    observer
  });
};

const observeRechargePriceWidgets = () => {
  document
    .querySelectorAll(RECHARGE_WIDGET_SELECTOR)
    .forEach(observeRechargePriceWidget);
};

const initializeRechargePriceSync = () => {
  const widgetMountObserver =
    new MutationObserver(() => {
      observeRechargePriceWidgets();
    });

  widgetMountObserver.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );

  observeRechargePriceWidgets();

  [100, 300, 750, 1500, 3000].forEach(
    (delay) => {
      window.setTimeout(() => {
        observeRechargePriceWidgets();
        syncProductPriceWithRecharge();
      }, delay);
    }
  );

  document.addEventListener(
    'variantChangeEnd',
    () => {
      restoreThemePrices();

      [50, 200, 600].forEach((delay) => {
        window.setTimeout(
          syncProductPriceWithRecharge,
          delay
        );
      });
    }
  );

  document.addEventListener(
    'shopify:section:load',
    () => {
      restoreThemePrices();
      scheduleRechargePriceSync(200);
    }
  );

  window.addEventListener(
    'resize',
    () => scheduleRechargePriceSync(80),
    { passive: true }
  );
};


/* =========================================================
   INITIALIZATION
   ========================================================= */

const initializeCustomScripts = () => {
  initializeRecharge();
  initializeRechargePriceSync();
  initializeSecondProductMedia();
};

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    initializeCustomScripts,
    { once: true }
  );
} else {
  initializeCustomScripts();
}

/*
 * Reinitialize when Shopify reloads a section inside
 * the theme editor.
 */
document.addEventListener(
  'shopify:section:load',
  () => {
    scheduleRechargeRefresh();
    initializeSecondProductMedia();
  }
);

document.addEventListener(
  'shopify:block:select',
  () => {
    scheduleRechargeRefresh();
  }
);

})();


/* =========================================================
   THE GOOD PATCH
   RECHARGE — STABLE 1 / 2 / 3 MONTH FREQUENCY PICKER
   ========================================================= */

(() => {
  'use strict';

  const WIDGET_SELECTOR = 'recharge-subscription-widget';
  const MONTHS_TO_SHOW = [1, 2, 3];

  let selectedFrequencyMonths = null;

  let refreshTimer = null;
  let commitTimer = null;
  let applyingRechargeChange = false;
  let pendingMonths = null;

  const widgetObservers = new WeakMap();


  /* =========================================================
     DEEP QUERY
     ========================================================= */

  const queryDeepAll = (
    root,
    selector,
    results = []
  ) => {
    if (
      !root ||
      typeof root.querySelectorAll !== 'function'
    ) {
      return results;
    }

    root
      .querySelectorAll(selector)
      .forEach((element) => {
        results.push(element);
      });

    root
      .querySelectorAll('*')
      .forEach((element) => {
        if (element.shadowRoot) {
          queryDeepAll(
            element.shadowRoot,
            selector,
            results
          );
        }
      });

    return results;
  };


  const getPartElement = (
    root,
    partName
  ) =>
    queryDeepAll(
      root,
      `[part~="${partName}"]`
    )[0] || null;


  const addPartToken = (
    element,
    token
  ) => {
    if (!element) return;

    const tokens = String(
      element.getAttribute('part') || ''
    )
      .split(/\s+/)
      .filter(Boolean);

    if (!tokens.includes(token)) {
      element.setAttribute(
        'part',
        [...tokens, token].join(' ')
      );
    }
  };


  const removePartToken = (
    element,
    token
  ) => {
    if (!element) return;

    const tokens = String(
      element.getAttribute('part') || ''
    )
      .split(/\s+/)
      .filter(Boolean);

    const updated =
      tokens.filter(
        (currentToken) =>
          currentToken !== token
      );

    if (updated.length) {
      element.setAttribute(
        'part',
        updated.join(' ')
      );
    } else {
      element.removeAttribute('part');
    }
  };


  /* =========================================================
     WIDGET HELPERS
     ========================================================= */

  const normalizeText = (value) =>
    String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();


  const getActiveWidget = () => {
    const widgets =
      Array.from(
        document.querySelectorAll(
          WIDGET_SELECTOR
        )
      );

    return (
      widgets.find((widget) => {
        const styles =
          window.getComputedStyle(widget);

        return (
          !widget.hidden &&
          styles.display !== 'none' &&
          styles.visibility !== 'hidden'
        );
      }) ||
      widgets[0] ||
      null
    );
  };


  const isVariationB = (widget) => {
    if (!widget?.shadowRoot) {
      return false;
    }

    const badge =
      getPartElement(
        widget.shadowRoot,
        'rc-purchase-option__badge'
      );

    if (!badge) {
      return false;
    }

    return normalizeText(
      badge.textContent
    ).includes(
      'subscribe & save'
    );
  };


  const getSellingPlanSelect = (
    widget
  ) => {
    if (!widget?.shadowRoot) {
      return null;
    }

    return (
      queryDeepAll(
        widget.shadowRoot,
        'select[part~="rc-plans-select"]'
      )[0] || null
    );
  };


  /* =========================================================
     FREQUENCY PARSING
     ========================================================= */

  const getOptionFrequency = (
    option
  ) => {
    if (!option) {
      return null;
    }

    const text =
      String(
        option.textContent || ''
      )
        .replace(/\s+/g, ' ')
        .trim();

    let match =
      text.match(
        /(?:deliver\s+)?every\s+(?:(\d+)\s+)?(day|week|month|year)s?/i
      );

    if (match) {
      return {
        amount:
          match[1]
            ? Number(match[1])
            : 1,
        unit:
          match[2]
            .toLowerCase()
      };
    }

    match =
      text.match(
        /(?:^|\b)(\d+)\s+(day|week|month|year)s?(?:\b|$)/i
      );

    if (!match) {
      return null;
    }

    return {
      amount:
        Number(match[1]),
      unit:
        match[2]
          .toLowerCase()
    };
  };


  const getMonthlyPlans = (
    select
  ) => {
    if (
      !(select instanceof HTMLSelectElement)
    ) {
      return [];
    }

    const matches =
      Array
        .from(select.options)
        .map((option) => ({
          option,
          frequency:
            getOptionFrequency(
              option
            )
        }))
        .filter(
          ({
            option,
            frequency
          }) =>
            option.value &&
            !option.disabled &&
            frequency &&
            frequency.unit ===
              'month' &&
            MONTHS_TO_SHOW.includes(
              frequency.amount
            )
        );

    /*
     * One plan per month interval.
     */
    const map =
      new Map();

    matches.forEach((plan) => {
      const month =
        plan.frequency.amount;

      if (
        !map.has(month) ||
        plan.option.selected
      ) {
        map.set(
          month,
          plan
        );
      }
    });

    return Array
      .from(
        map.values()
      )
      .sort(
        (a, b) =>
          a.frequency.amount -
          b.frequency.amount
      );
  };


  /* =========================================================
     SUBSCRIPTION ELEMENTS
     ========================================================= */

  const getSubscriptionElements = (
    widget
  ) => {
    if (!widget?.shadowRoot) {
      return {};
    }

    const subscriptionOption =
      getPartElement(
        widget.shadowRoot,
        'rc-purchase-option__subscription'
      );

    const selector =
      subscriptionOption
        ? getPartElement(
            subscriptionOption,
            'rc-purchase-option__selector_subscription'
          )
        : null;

    const nativeInput =
      selector
        ? getPartElement(
            selector,
            'rc-purchase-option__input'
          )
        : null;

    const nativeLabel =
      selector
        ? getPartElement(
            selector,
            'rc-purchase-option__label'
          )
        : null;

    return {
      subscriptionOption,
      selector,
      nativeInput,
      nativeLabel
    };
  };


  const hideNativeHeading = (
    widget
  ) => {
    const {
      nativeInput,
      nativeLabel
    } =
      getSubscriptionElements(
        widget
      );

    [
      nativeInput,
      nativeLabel
    ].forEach((element) => {
      if (!element) return;

      element.style.setProperty(
        'display',
        'none',
        'important'
      );

      element.setAttribute(
        'aria-hidden',
        'true'
      );
    });
  };


  /* =========================================================
     VISUAL SELECTED STATE
     ========================================================= */

  const getFrequencyLabel = (
    months
  ) =>
    months === 1
      ? 'Every 1 Month'
      : `Every ${months} Months`;


  const syncActiveButtons = (
    container,
    months
  ) => {
    if (!container) {
      return;
    }

    queryDeepAll(
      container,
      '[part~="tgp-delivery-frequency-option"]'
    ).forEach((button) => {

      const buttonMonths =
        Number(
          button.dataset
            .tgpMonths
        );

      const active =
        buttonMonths === months;


      if (active) {
        addPartToken(
          button,
          'tgp-delivery-frequency-option-active'
        );
      } else {
        removePartToken(
          button,
          'tgp-delivery-frequency-option-active'
        );
      }


      button.setAttribute(
        'aria-checked',
        active
          ? 'true'
          : 'false'
      );


      button.style.setProperty(
        'background',
        active
          ? '#F1DADD'
          : 'rgba(255,255,255,.16)',
        'important'
      );

      button.style.setProperty(
        'border-color',
        active
          ? '#A02678'
          : 'rgba(160,38,120,.25)',
        'important'
      );

      button.style.setProperty(
        'color',
        active
          ? '#A02678'
          : '#2A234B',
        'important'
      );

      button.style.setProperty(
        'font-weight',
        active
          ? '700'
          : '600',
        'important'
      );


      const radio =
        button.querySelector(
          '[part~="tgp-delivery-frequency-radio"]'
        );

      if (radio) {
        radio.style.setProperty(
          'border-color',
          '#A02678',
          'important'
        );

        radio.style.setProperty(
          'background-color',
          active
            ? '#A02678'
            : '#FFFFFF',
          'important'
        );

        radio.style.setProperty(
          'background-position',
          'center',
          'important'
        );

        radio.style.setProperty(
          'background-repeat',
          'no-repeat',
          'important'
        );

        radio.style.setProperty(
          'background-size',
          '11px 11px',
          'important'
        );

        radio.style.setProperty(
          'background-image',
          active
            ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23FFFFFF' stroke-width='3.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'/%3E%3C/svg%3E")`
            : 'none',
          'important'
        );
      }


      const label =
        button.querySelector(
          '[part~="tgp-delivery-frequency-text"]'
        );

      if (label) {
        label.style.setProperty(
          'color',
          active
            ? '#A02678'
            : '#2A234B',
          'important'
        );

        label.style.setProperty(
          'font-weight',
          active
            ? '700'
            : '600',
          'important'
        );
      }
    });
  };


  /* =========================================================
     DEBOUNCED RECHARGE UPDATE
     ========================================================= */

  const finishRechargeChange = () => {
    applyingRechargeChange =
      false;

    scheduleRefresh(
      20
    );

    /*
     * If the shopper clicked another option while
     * Recharge was updating, apply only the newest one.
     */
    if (
      pendingMonths !== null
    ) {
      queueRechargeSelection(
        pendingMonths,
        80
      );
    }
  };


  const commitRechargeSelection =
    () => {

      if (
        pendingMonths === null
      ) {
        return;
      }

      /*
       * Recharge is still processing the previous
       * change. Wait rather than firing another one.
       */
      if (applyingRechargeChange) {
        commitTimer =
          window.setTimeout(
            commitRechargeSelection,
            100
          );

        return;
      }


      const months =
        pendingMonths;

      pendingMonths =
        null;


      const widget =
        getActiveWidget();

      if (
        !widget ||
        !widget.shadowRoot
      ) {
        return;
      }


      const {
        nativeInput
      } =
        getSubscriptionElements(
          widget
        );


      /*
       * Switch from One-Time to Subscription first.
       *
       * Do not attempt to change the selling plan in
       * the same instant Recharge is changing purchase mode.
       */
      if (
        nativeInput &&
        !nativeInput.checked
      ) {
        applyingRechargeChange =
          true;

        nativeInput.click();

        window.setTimeout(
          () => {
            applyingRechargeChange =
              false;

            pendingMonths =
              months;

            queueRechargeSelection(
              months,
              180
            );
          },
          220
        );

        return;
      }


      const select =
        getSellingPlanSelect(
          widget
        );

      if (!select) {
        pendingMonths =
          months;

        queueRechargeSelection(
          months,
          120
        );

        return;
      }


      const plans =
        getMonthlyPlans(
          select
        );

      const target =
        plans.find(
          ({ frequency }) =>
            frequency.amount ===
            months
        );


      if (!target) {
        return;
      }


      /*
       * If Recharge is already on this frequency,
       * do not dispatch another change event.
       */
      const currentOption =
        select.options[
          select.selectedIndex
        ];

      const currentFrequency =
        getOptionFrequency(
          currentOption
        );

      if (
        currentFrequency?.unit ===
          'month' &&
        currentFrequency.amount ===
          months
      ) {
        scheduleRefresh(
          20
        );

        return;
      }


      const targetIndex =
        Array
          .from(
            select.options
          )
          .indexOf(
            target.option
          );

      if (
        targetIndex < 0
      ) {
        return;
      }


      applyingRechargeChange =
        true;


      select.selectedIndex =
        targetIndex;

      select.value =
        target.option.value;


      /*
       * Fire ONE Recharge event.
       *
       * No input event.
       * No duplicate retries.
       * No multiple selling-plan writes.
       */
      select.dispatchEvent(
        new Event(
          'change',
          {
            bubbles: true,
            composed: true
          }
        )
      );


      /*
       * Give Recharge time to finish its own rerender
       * before allowing another selling-plan change.
       */
      window.setTimeout(
        finishRechargeChange,
        300
      );
    };


  function queueRechargeSelection(
    months,
    delay = 120
  ) {
    if (
      !MONTHS_TO_SHOW.includes(
        Number(months)
      )
    ) {
      return;
    }

    pendingMonths =
      Number(months);

    window.clearTimeout(
      commitTimer
    );

    commitTimer =
      window.setTimeout(
        commitRechargeSelection,
        delay
      );
  }


  /* =========================================================
     BUILD BUTTONS
     ========================================================= */

  const buildButtons = ({
    widget,
    container,
    plans
  }) => {

    const optionsWrap =
      document.createElement(
        'div'
      );

    optionsWrap.setAttribute(
      'part',
      'tgp-delivery-frequency-options'
    );

    optionsWrap.setAttribute(
      'role',
      'radiogroup'
    );

    optionsWrap.setAttribute(
      'aria-label',
      'Choose delivery frequency'
    );


    plans.forEach(
      ({ frequency }) => {

        const button =
          document.createElement(
            'button'
          );

        button.type =
          'button';

        button.setAttribute(
          'part',
          'tgp-delivery-frequency-option'
        );

        button.setAttribute(
          'role',
          'radio'
        );

        button.dataset
          .tgpMonths =
          String(
            frequency.amount
          );


        const radio =
          document.createElement(
            'span'
          );

        radio.setAttribute(
          'part',
          'tgp-delivery-frequency-radio'
        );

        radio.setAttribute(
          'aria-hidden',
          'true'
        );


        const text =
          document.createElement(
            'span'
          );

        text.setAttribute(
          'part',
          'tgp-delivery-frequency-text'
        );

        text.textContent =
          getFrequencyLabel(
            frequency.amount
          );


        button.append(
          radio,
          text
        );


        button.addEventListener(
          'click',
          (event) => {

            event.preventDefault();
            event.stopPropagation();

            const months =
              Number(
                button.dataset
                  .tgpMonths
              );

            /*
             * Customer feedback is immediate.
             */
            selectedFrequencyMonths =
              months;

            syncActiveButtons(
              container,
              months
            );


            /*
             * Recharge receives the final choice only
             * after clicks have settled.
             */
            queueRechargeSelection(
              months
            );
          }
        );


        optionsWrap.appendChild(
          button
        );
      }
    );


    container.appendChild(
      optionsWrap
    );
  };


  /* =========================================================
     WATCH RECHARGE'S SHADOW DOM
     ========================================================= */

  const observeWidgetShadowRoot = (
    widget
  ) => {
    if (
      !widget?.shadowRoot ||
      widgetObservers.has(widget)
    ) {
      return;
    }

    const observer =
      new MutationObserver(
        () => {
          scheduleRefresh(
            80
          );
        }
      );

    observer.observe(
      widget.shadowRoot,
      {
        childList: true,
        subtree: true
      }
    );

    widgetObservers.set(
      widget,
      observer
    );
  };


  /* =========================================================
     BUILD / REFRESH UI
     ========================================================= */

  const syncWidget = (
    widget
  ) => {
    if (
      !widget?.shadowRoot
    ) {
      return;
    }

    observeWidgetShadowRoot(
      widget
    );


    if (
      !isVariationB(widget)
    ) {
      return;
    }


    const {
      selector
    } =
      getSubscriptionElements(
        widget
      );

    const select =
      getSellingPlanSelect(
        widget
      );


    /*
     * Recharge can temporarily remove these while it
     * rerenders. Do nothing and wait for the shadow
     * observer to call us again.
     */
    if (
      !selector ||
      !select
    ) {
      return;
    }


    const plans =
      getMonthlyPlans(
        select
      );


    if (!plans.length) {
      return;
    }


    hideNativeHeading(
      widget
    );


    const availableMonths =
      plans.map(
        ({ frequency }) =>
          frequency.amount
      );


    const signature =
      availableMonths
        .map(String)
        .join('|');


    let container =
      getPartElement(
        widget.shadowRoot,
        'tgp-delivery-frequency'
      );


    if (!container) {
      container =
        document.createElement(
          'div'
        );

      container.setAttribute(
        'part',
        'tgp-delivery-frequency'
      );

      selector.appendChild(
        container
      );
    }


    if (
      container.dataset
        .tgpPlanSignature !==
      signature
    ) {

      container.replaceChildren();


      const heading =
        document.createElement(
          'div'
        );

      heading.setAttribute(
        'part',
        'tgp-delivery-frequency-label'
      );

      heading.textContent =
        'Choose delivery frequency:';

      container.appendChild(
        heading
      );


      buildButtons({
        widget,
        container,
        plans
      });


      const help =
        document.createElement(
          'div'
        );

      help.setAttribute(
        'part',
        'tgp-delivery-frequency-help'
      );

      help.textContent =
        'Same great price. Choose how often to receive your delivery.';

      container.appendChild(
        help
      );


      container.dataset
        .tgpPlanSignature =
        signature;
    }


    /*
     * Native Recharge selection is used on first load.
     * After the shopper clicks, our remembered frequency
     * controls the visual state.
     */
    let activeMonths =
      selectedFrequencyMonths;


    if (
      !MONTHS_TO_SHOW.includes(
        activeMonths
      )
    ) {
      const currentOption =
        select.options[
          select.selectedIndex
        ];

      const nativeFrequency =
        getOptionFrequency(
          currentOption
        );

      activeMonths =
        nativeFrequency?.unit ===
          'month'
          ? nativeFrequency.amount
          : null;
    }


    if (
      !availableMonths.includes(
        activeMonths
      )
    ) {
      activeMonths =
        availableMonths[0] ||
        null;
    }


    syncActiveButtons(
      container,
      activeMonths
    );
  };


  /* =========================================================
     REFRESH
     ========================================================= */

  const refresh = () => {
    document
      .querySelectorAll(
        WIDGET_SELECTOR
      )
      .forEach(
        (widget) => {
          syncWidget(
            widget
          );
        }
      );
  };


  function scheduleRefresh(
    delay = 60
  ) {
    window.clearTimeout(
      refreshTimer
    );

    refreshTimer =
      window.setTimeout(
        refresh,
        delay
      );
  }


  /* =========================================================
     OUTER DOM / SHOPIFY EVENTS
     ========================================================= */

  const pageObserver =
    new MutationObserver(
      () => {
        scheduleRefresh(
          100
        );
      }
    );


  pageObserver.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );


  document.addEventListener(
    'variantChangeEnd',
    () => {
      [
        80,
        250,
        600
      ].forEach(
        (delay) => {
          window.setTimeout(
            refresh,
            delay
          );
        }
      );
    }
  );


  document.addEventListener(
    'shopify:section:load',
    () => {
      scheduleRefresh(
        120
      );
    }
  );


  window.addEventListener(
    'pageshow',
    () => {
      scheduleRefresh(
        80
      );
    }
  );


  /* =========================================================
     INITIALIZE
     ========================================================= */

  refresh();

  [
    150,
    400,
    1000,
    2000
  ].forEach(
    (delay) => {
      window.setTimeout(
        refresh,
        delay
      );
    }
  );


  if (
    'customElements' in window
  ) {
    customElements
      .whenDefined(
        WIDGET_SELECTOR
      )
      .then(
        () => {
          scheduleRefresh(
            0
          );
        }
      )
      .catch(() => {});
  }

})();